import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  LifePlanState,
  PersonalInfo,
  FamilyInfo,
  CareerInfo,
  Asset,
  LifePlanDebt,
  InsuranceInfo,
  TimelineGoal,
  Preferences,
  Child,
  PlannedChild,
  Dependent,
  SideIncome,
  PlannedCareerChange,
  LifeInsurance,
  SpouseInfo,
  JobInfo,
} from '@/types'
import { createInitialLifePlan } from '@/types/lifePlan.types'

const initialState: LifePlanState = createInitialLifePlan()

// Helper to recalculate computed values
function recalculateComputed(state: LifePlanState) {
  const totalAssets = state.assets.reduce((sum, a) => sum + a.currentValue, 0)
  const totalDebts = state.debts.reduce((sum, d) => sum + d.balance, 0)
  
  // Calculate monthly income from career
  let monthlyIncome = 0
  if (state.career.currentJob) {
    const job = state.career.currentJob
    monthlyIncome += (job.baseSalary + job.bonus + job.commission + job.stockCompensation) / 12
  }
  state.career.sideIncomes.forEach(si => {
    if (si.isRecurring) {
      monthlyIncome += si.annualAmount / 12
    }
  })
  if (state.family.spouse) {
    monthlyIncome += state.family.spouse.currentIncome / 12
  }
  
  // Calculate years to retirement
  let age = 0
  if (state.personal.birthDate) {
    const birth = new Date(state.personal.birthDate)
    const today = new Date()
    age = today.getFullYear() - birth.getFullYear()
  }
  const yearsToRetirement = Math.max(0, state.personal.retirementAge - age)
  
  // Estimate monthly expenses from debts + insurance
  const monthlyDebtPayments = state.debts.reduce((sum, d) => sum + d.minimumPayment + d.extraPayment, 0)
  const monthlyInsurance = (
    state.insurance.health.monthlyPremium +
    state.insurance.life.reduce((sum, l) => sum + l.monthlyPremium, 0) +
    state.insurance.disability.monthlyPremium +
    (state.insurance.autoAnnual + state.insurance.homeAnnual + state.insurance.rentersAnnual + state.insurance.umbrellaAnnual) / 12
  )
  const monthlyExpenses = monthlyDebtPayments + monthlyInsurance  // Will be enhanced with budget data
  
  const monthlySavings = monthlyIncome - monthlyExpenses
  const savingsRate = monthlyIncome > 0 ? (monthlySavings / monthlyIncome) * 100 : 0
  
  state.computed = {
    totalAssets,
    totalDebts,
    netWorth: totalAssets - totalDebts,
    monthlyIncome,
    monthlyExpenses,
    savingsRate: Math.max(0, savingsRate),
    yearsToRetirement,
  }
  
  state.updatedAt = new Date().toISOString()
}

// Helper to generate unique IDs
function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

const lifePlanSlice = createSlice({
  name: 'lifePlan',
  initialState,
  reducers: {
    // ========================================
    // PERSONAL INFO
    // ========================================
    updatePersonalInfo: (state, action: PayloadAction<Partial<PersonalInfo>>) => {
      state.personal = { ...state.personal, ...action.payload }
      recalculateComputed(state)
    },
    
    // ========================================
    // FAMILY
    // ========================================
    updateFamilyInfo: (state, action: PayloadAction<Partial<FamilyInfo>>) => {
      state.family = { ...state.family, ...action.payload }
      recalculateComputed(state)
    },
    
    setSpouse: (state, action: PayloadAction<SpouseInfo | undefined>) => {
      state.family.spouse = action.payload
      recalculateComputed(state)
    },
    
    addChild: (state, action: PayloadAction<Omit<Child, 'id'>>) => {
      state.family.children.push({
        ...action.payload,
        id: generateId('child'),
      })
    },
    
    updateChild: (state, action: PayloadAction<{ id: string; updates: Partial<Child> }>) => {
      const child = state.family.children.find(c => c.id === action.payload.id)
      if (child) {
        Object.assign(child, action.payload.updates)
      }
    },
    
    removeChild: (state, action: PayloadAction<string>) => {
      state.family.children = state.family.children.filter(c => c.id !== action.payload)
    },
    
    addPlannedChild: (state, action: PayloadAction<Omit<PlannedChild, 'id'>>) => {
      state.family.plannedChildren.push({
        ...action.payload,
        id: generateId('planned_child'),
      })
    },
    
    updatePlannedChild: (state, action: PayloadAction<{ id: string; updates: Partial<PlannedChild> }>) => {
      const child = state.family.plannedChildren.find(c => c.id === action.payload.id)
      if (child) {
        Object.assign(child, action.payload.updates)
      }
    },
    
    removePlannedChild: (state, action: PayloadAction<string>) => {
      state.family.plannedChildren = state.family.plannedChildren.filter(c => c.id !== action.payload)
    },
    
    addDependent: (state, action: PayloadAction<Omit<Dependent, 'id'>>) => {
      state.family.dependents.push({
        ...action.payload,
        id: generateId('dependent'),
      })
    },
    
    removeDependent: (state, action: PayloadAction<string>) => {
      state.family.dependents = state.family.dependents.filter(d => d.id !== action.payload)
    },
    
    // ========================================
    // CAREER
    // ========================================
    updateCareerInfo: (state, action: PayloadAction<Partial<CareerInfo>>) => {
      state.career = { ...state.career, ...action.payload }
      recalculateComputed(state)
    },
    
    setCurrentJob: (state, action: PayloadAction<JobInfo | undefined>) => {
      state.career.currentJob = action.payload
      recalculateComputed(state)
    },
    
    addSideIncome: (state, action: PayloadAction<Omit<SideIncome, 'id'>>) => {
      state.career.sideIncomes.push({
        ...action.payload,
        id: generateId('side_income'),
      })
      recalculateComputed(state)
    },
    
    updateSideIncome: (state, action: PayloadAction<{ id: string; updates: Partial<SideIncome> }>) => {
      const income = state.career.sideIncomes.find(i => i.id === action.payload.id)
      if (income) {
        Object.assign(income, action.payload.updates)
        recalculateComputed(state)
      }
    },
    
    removeSideIncome: (state, action: PayloadAction<string>) => {
      state.career.sideIncomes = state.career.sideIncomes.filter(i => i.id !== action.payload)
      recalculateComputed(state)
    },
    
    addPlannedCareerChange: (state, action: PayloadAction<Omit<PlannedCareerChange, 'id'>>) => {
      state.career.plannedChanges.push({
        ...action.payload,
        id: generateId('career_change'),
      })
    },
    
    removePlannedCareerChange: (state, action: PayloadAction<string>) => {
      state.career.plannedChanges = state.career.plannedChanges.filter(c => c.id !== action.payload)
    },
    
    // ========================================
    // ASSETS
    // ========================================
    addAsset: (state, action: PayloadAction<Omit<Asset, 'id'>>) => {
      state.assets.push({
        ...action.payload,
        id: generateId('asset'),
      })
      recalculateComputed(state)
    },
    
    updateAsset: (state, action: PayloadAction<{ id: string; updates: Partial<Asset> }>) => {
      const asset = state.assets.find(a => a.id === action.payload.id)
      if (asset) {
        Object.assign(asset, action.payload.updates)
        recalculateComputed(state)
      }
    },
    
    removeAsset: (state, action: PayloadAction<string>) => {
      state.assets = state.assets.filter(a => a.id !== action.payload)
      recalculateComputed(state)
    },
    
    // ========================================
    // DEBTS
    // ========================================
    addDebt: (state, action: PayloadAction<Omit<LifePlanDebt, 'id'>>) => {
      state.debts.push({
        ...action.payload,
        id: generateId('debt'),
      })
      recalculateComputed(state)
    },
    
    updateDebt: (state, action: PayloadAction<{ id: string; updates: Partial<LifePlanDebt> }>) => {
      const debt = state.debts.find(d => d.id === action.payload.id)
      if (debt) {
        Object.assign(debt, action.payload.updates)
        recalculateComputed(state)
      }
    },
    
    removeDebt: (state, action: PayloadAction<string>) => {
      state.debts = state.debts.filter(d => d.id !== action.payload)
      recalculateComputed(state)
    },
    
    // ========================================
    // INSURANCE
    // ========================================
    updateInsurance: (state, action: PayloadAction<Partial<InsuranceInfo>>) => {
      state.insurance = { ...state.insurance, ...action.payload }
      recalculateComputed(state)
    },
    
    addLifeInsurance: (state, action: PayloadAction<Omit<LifeInsurance, 'id'>>) => {
      state.insurance.life.push({
        ...action.payload,
        id: generateId('life_insurance'),
      })
      recalculateComputed(state)
    },
    
    updateLifeInsurance: (state, action: PayloadAction<{ id: string; updates: Partial<LifeInsurance> }>) => {
      const policy = state.insurance.life.find(l => l.id === action.payload.id)
      if (policy) {
        Object.assign(policy, action.payload.updates)
        recalculateComputed(state)
      }
    },
    
    removeLifeInsurance: (state, action: PayloadAction<string>) => {
      state.insurance.life = state.insurance.life.filter(l => l.id !== action.payload)
      recalculateComputed(state)
    },
    
    // ========================================
    // GOALS
    // ========================================
    addGoal: (state, action: PayloadAction<Omit<TimelineGoal, 'id'>>) => {
      state.goals.push({
        ...action.payload,
        id: generateId('goal'),
      })
    },
    
    updateGoal: (state, action: PayloadAction<{ id: string; updates: Partial<TimelineGoal> }>) => {
      const goal = state.goals.find(g => g.id === action.payload.id)
      if (goal) {
        Object.assign(goal, action.payload.updates)
      }
    },
    
    removeGoal: (state, action: PayloadAction<string>) => {
      state.goals = state.goals.filter(g => g.id !== action.payload)
    },
    
    // ========================================
    // PREFERENCES
    // ========================================
    updatePreferences: (state, action: PayloadAction<Partial<Preferences>>) => {
      state.preferences = { ...state.preferences, ...action.payload }
    },
    
    // ========================================
    // QUESTIONNAIRE PROGRESS
    // ========================================
    setQuestionnaireCompleted: (state, action: PayloadAction<boolean>) => {
      state.questionnaireCompleted = action.payload
    },
    
    setQuestionnaireProgress: (state, action: PayloadAction<number>) => {
      state.questionnaireProgress = action.payload
    },
    
    // ========================================
    // FULL STATE OPERATIONS
    // ========================================
    setLifePlanData: (state, action: PayloadAction<Partial<LifePlanState>>) => {
      Object.assign(state, action.payload)
      recalculateComputed(state)
    },
    
    resetLifePlan: () => createInitialLifePlan(),
    
    // Import from legacy data
    importFromLegacy: (state, action: PayloadAction<{
      portfolio?: { totalValue: number; investments: Array<{ name: string; value: number }> }
      debt?: { debts: Array<{ name: string; balance: number; interestRate: number; monthlyPayment: number }> }
      budget?: { grossIncome: number }
      retirement?: { currentAge: number; retirementAge: number }
    }>) => {
      const { portfolio, debt, budget, retirement } = action.payload
      
      // Import portfolio as assets
      if (portfolio?.investments) {
        portfolio.investments.forEach(inv => {
          state.assets.push({
            id: generateId('asset'),
            name: inv.name,
            category: 'brokerage',
            currentValue: inv.value,
            expectedReturn: 7,
            contributions: 0,
            isRetirementAccount: false,
            taxAdvantaged: false,
          })
        })
      }
      
      // Import debts
      if (debt?.debts) {
        debt.debts.forEach(d => {
          state.debts.push({
            id: generateId('debt'),
            name: d.name,
            category: 'other',
            balance: d.balance,
            interestRate: d.interestRate,
            minimumPayment: d.monthlyPayment,
            extraPayment: 0,
          })
        })
      }
      
      // Set income if available
      if (budget?.grossIncome && !state.career.currentJob) {
        state.career.currentJob = {
          title: 'Current Job',
          employer: '',
          industry: 'Other',
          yearsInRole: 0,
          yearsExperience: 0,
          baseSalary: budget.grossIncome,
          bonus: 0,
          commission: 0,
          stockCompensation: 0,
          employerMatch401k: 0,
          employerMatchLimit: 0,
        }
      }
      
      // Set retirement info
      if (retirement) {
        if (retirement.currentAge) {
          const birthYear = new Date().getFullYear() - retirement.currentAge
          state.personal.birthDate = `${birthYear}-01-01`
        }
        if (retirement.retirementAge) {
          state.personal.retirementAge = retirement.retirementAge
        }
      }
      
      recalculateComputed(state)
    },
  },
})

export const {
  updatePersonalInfo,
  updateFamilyInfo,
  setSpouse,
  addChild,
  updateChild,
  removeChild,
  addPlannedChild,
  updatePlannedChild,
  removePlannedChild,
  addDependent,
  removeDependent,
  updateCareerInfo,
  setCurrentJob,
  addSideIncome,
  updateSideIncome,
  removeSideIncome,
  addPlannedCareerChange,
  removePlannedCareerChange,
  addAsset,
  updateAsset,
  removeAsset,
  addDebt,
  updateDebt,
  removeDebt,
  updateInsurance,
  addLifeInsurance,
  updateLifeInsurance,
  removeLifeInsurance,
  addGoal,
  updateGoal,
  removeGoal,
  updatePreferences,
  setQuestionnaireCompleted,
  setQuestionnaireProgress,
  setLifePlanData,
  resetLifePlan,
  importFromLegacy,
} = lifePlanSlice.actions

export default lifePlanSlice.reducer
